import { useMemo, useState } from "react";

import { Tooltip } from "@/components/Tooltip";
import type { WidgetComponentProps } from "../types";
import { useSummaryQuery } from "../useSummaryQuery";

const DAY_MS = 24 * 3600 * 1000;

type HeatmapRange = "1w" | "1m" | "3m" | "6m" | "1y";

const RANGE_OPTIONS: Array<{ key: HeatmapRange; label: string; weeks: number }> = [
  { key: "1w", label: "1W", weeks: 1 },
  { key: "1m", label: "1M", weeks: 5 },
  { key: "3m", label: "3M", weeks: 13 },
  { key: "6m", label: "6M", weeks: 26 },
  { key: "1y", label: "1Y", weeks: 53 },
];

function defaultRangeForSize(sizeVariant: string): HeatmapRange {
  if (sizeVariant === "s" || sizeVariant === "xs") return "1m";
  if (sizeVariant === "m") return "3m";
  return "6m";
}

interface HeatmapCell {
  date: Date;
  count: number;
  isFuture: boolean;
}

function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildGrid(flightDates: string[], weeks: number) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Weeks start Monday (ISO). Find the Sunday that closes the current week so we
  // can walk backwards by 7-day blocks Mon→Sun.
  const endOfWeek = new Date(today);
  // getDay(): Sun=0, Mon=1 … Sat=6. Days until next Sunday (inclusive today).
  const daysToSunday = (7 - endOfWeek.getDay()) % 7;
  endOfWeek.setDate(endOfWeek.getDate() + daysToSunday);

  const counts = new Map<string, number>();
  for (const date of flightDates) {
    const key = date.slice(0, 10);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const columns: HeatmapCell[][] = [];
  for (let w = weeks - 1; w >= 0; w--) {
    // Monday of this column.
    const weekStart = new Date(endOfWeek.getTime() - (w * 7 + 6) * DAY_MS);
    const days: HeatmapCell[] = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(weekStart.getTime() + d * DAY_MS);
      const key = toLocalDateKey(day);
      days.push({
        date: day,
        count: counts.get(key) ?? 0,
        isFuture: day.getTime() > today.getTime(),
      });
    }
    columns.push(days);
  }

  let maxCount = 0;
  counts.forEach((v) => {
    if (v > maxCount) maxCount = v;
  });
  return { columns, maxCount };
}

function intensityClass(count: number, max: number): string {
  if (count <= 0) return "heat-0";
  if (max <= 1) return "heat-4";
  const ratio = count / max;
  if (ratio > 0.75) return "heat-4";
  if (ratio > 0.5) return "heat-3";
  if (ratio > 0.25) return "heat-2";
  return "heat-1";
}

const DAY_LABELS = ["Mon", "", "Wed", "", "Fri", "", "Sun"];
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function ActivityHeatmapWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();

  const [range, setRange] = useState<HeatmapRange>(() =>
    defaultRangeForSize(sizeVariant),
  );

  const weeks =
    RANGE_OPTIONS.find((o) => o.key === range)?.weeks ??
    RANGE_OPTIONS[3].weeks;

  const grid = useMemo(
    () => buildGrid(data?.flight_dates ?? [], weeks),
    [data?.flight_dates, weeks],
  );

  const totalInWindow = useMemo(() => {
    let total = 0;
    for (const col of grid.columns) {
      for (const cell of col) {
        if (!cell.isFuture) total += cell.count;
      }
    }
    return total;
  }, [grid]);

  const monthLabels = useMemo(() => {
    if (sizeVariant !== "l" || weeks < 8) return [];
    const labels: Array<{ col: number; text: string }> = [];
    let prevMonth = -1;
    grid.columns.forEach((col, idx) => {
      const firstDay = col[0];
      if (firstDay && firstDay.date.getMonth() !== prevMonth) {
        prevMonth = firstDay.date.getMonth();
        labels.push({ col: idx, text: MONTH_NAMES[prevMonth] });
      }
    });
    return labels;
  }, [grid.columns, sizeVariant]);

  if (isLoading) return <p className="widget-empty">Loading…</p>;

  return (
    <div className={`heatmap heatmap--${sizeVariant}`}>
      <div className="heatmap__head">
        <div className="heatmap__head-info">
          <p className="eyebrow">
            Activity · {RANGE_OPTIONS.find((o) => o.key === range)?.label}
          </p>
          <span className="heatmap__total">
            {totalInWindow} flight{totalInWindow === 1 ? "" : "s"}
          </span>
        </div>
        <div
          className="heatmap__range"
          role="group"
          aria-label="Time range"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`heatmap__range-btn ${
                range === opt.key ? "heatmap__range-btn--active" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setRange(opt.key);
              }}
              title={`Show last ${opt.label}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="heatmap__wrap">
        {sizeVariant === "l" && monthLabels.length > 0 && (
          <div className="heatmap__months">
            {monthLabels.map((m) => (
              <span
                key={m.col + m.text}
                className="heatmap__month"
                style={{ gridColumnStart: m.col + 2 }}
              >
                {m.text}
              </span>
            ))}
          </div>
        )}
        <div className="heatmap__main">
          <div className="heatmap__day-labels">
            {DAY_LABELS.map((lbl, i) => (
              <span key={i} className="heatmap__day-label">
                {lbl}
              </span>
            ))}
          </div>
          <div className="heatmap__grid">
            {grid.columns.map((week, wIdx) => (
              <div key={wIdx} className="heatmap__col">
                {week.map((day, dIdx) => {
                  if (day.isFuture) {
                    return (
                      <span
                        key={dIdx}
                        className="heatmap__cell heatmap__cell--future"
                      />
                    );
                  }
                  const dayName = day.date.toLocaleDateString(undefined, {
                    weekday: "short",
                  });
                  const fullDate = day.date.toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  });
                  return (
                    <Tooltip
                      key={dIdx}
                      content={
                        <>
                          <strong>{fullDate}</strong>
                          <span>
                            {day.count === 0
                              ? `No flights · ${dayName}`
                              : `${day.count} flight${day.count === 1 ? "" : "s"} · ${dayName}`}
                          </span>
                        </>
                      }
                    >
                      <span
                        className={`heatmap__cell ${intensityClass(day.count, grid.maxCount)}`}
                      />
                    </Tooltip>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="heatmap__legend">
        <span>Less</span>
        <span className="heatmap__cell heat-0" />
        <span className="heatmap__cell heat-1" />
        <span className="heatmap__cell heat-2" />
        <span className="heatmap__cell heat-3" />
        <span className="heatmap__cell heat-4" />
        <span>More</span>
      </div>
    </div>
  );
}
